import React from 'react'
import * as yup from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'
import useFormWithErrors from '@hooks/useFormWithErrors'

import { Button } from '@atoms'

import EditableTextFieldRow from './EditableTextFieldRow'
import useSupporterUpdateMutation from './hooks/useSupporterUpdateMutation'
import { scopedTranslation } from '@utils/I18n'

const tShared = scopedTranslation('shared')
const tAttributes = scopedTranslation('attributes')

type SMSPhoneFieldRowType = {
  supporterId: string | number
  initialValue: string | null
  canEdit?: boolean
}

export default function SmsPhoneFieldRow({ supporterId, initialValue, canEdit }: SMSPhoneFieldRowType) {
  const [open, setOpen] = React.useState(false)
  const [value, setValue] = React.useState(initialValue)

  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const editButtonRef = React.useRef<HTMLButtonElement | null>(null)

  const validations = yup.object().shape({
    supporter: yup.object({
      mobile: yup.string().label(tAttributes('supporter.mobile')),
    }),
  })

  const {
    setErrors: setFormErrors,
    register,
    handleSubmit,
    control,
  } = useFormWithErrors({
    resolver: yupResolver(validations),
    mode: 'onTouched',
    defaultValues: {
      supporter: { mobile: '' },
    },
  })

  const inputProps = register('supporter.mobile')

  const { onSubmit } = useSupporterUpdateMutation({
    setFormErrors,
    supporterId,
    onSuccess: (response) => {
      setOpen(false)
      setValue(response.updateSupporter?.supporter?.primaryMobile ?? '')
      editButtonRef.current?.focus()
    },
  })

  const onCancel = () => {
    setOpen(false)
    setTimeout(() => {
      editButtonRef.current?.focus()
    })
  }

  const onEdit = () => {
    setOpen(true)
    setTimeout(() => {
      inputRef.current?.focus()
    })
  }

  return (
    <div
      className="tw:grid tw:grid-cols-5 tw:grid-rows-1 tw:gap-4 tw:w-full tw:py-3 align-items-center"
      data-compid="supporter-show_mobile-field-row"
    >
      <div>
        <p className="tw:m-0 tw:text-gray-900 tw:font-medium tw:text-sm">{tAttributes('supporter.mobile')}</p>
      </div>
      {open ? (
        <div className="tw:col-span-2">
          <EditableTextFieldRow
            {...inputProps}
            onSubmit={handleSubmit(onSubmit)}
            label={tAttributes('supporter.mobile')}
            open={open}
            control={control}
            ref={(el) => {
              inputRef.current = el
              inputProps.ref(el)
            }}
            onCancel={onCancel}
          />
        </div>
      ) : (
        <div className="tw:col-span-3">
          <p>{value}</p>
        </div>
      )}

      <div className="tw:col-start-5 tw:flex tw:justify-end">
        <Button
          rank="secondary"
          leadingIcon="pencil-02"
          onClick={onEdit}
          ref={editButtonRef}
          ariaLabel={tShared('aria_labels.edit_object', { name: tAttributes('supporter.mobile') })}
          disabled={canEdit ? open : true}
          dataCompId="supporter-show_mobile-field-row_edit"
        >
          {tShared('buttons.edit')}
        </Button>
      </div>
    </div>
  )
}
